import { computed, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { defineProduct, useSchemaOrg } from '@unhead/schema-org/vue'

import type {
    MetaFragment,
    Money,
    ProductPageMetaFragment,
} from '@/graphql/api'
import { useMeta } from '@/composables/app/useMeta'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl'
import { assertNever } from '@/helpers'
import type { Review } from '@unhead/schema-org'
import { parseDateTime } from '@/utils/date'
import { type RingBuilder } from '@/composables/useRingBuilder.ts'

function getTitleForProduct(
    product: ProductPageMetaFragment | undefined | null
): string | undefined {
    if (!product) {
        return undefined
    }

    switch (product?.__typename) {
        case 'CatalogueEngagementRing':
            return `${product.productName} • ${product.primaryMetal.translation.name} ${product.erRingStyles[0]?.translation.name ? product.erRingStyles[0]?.translation.name + ' Style' : ''}`
        case 'CatalogueWeddingRing':
            return `${product.productName} • ${product.primaryMetal.translation.name} ${product.wrRingStyles[0]?.translation.name || ''}`
        case 'CatalogueJewelleryBracelet':
        case 'CatalogueJewelleryEarrings':
        case 'CatalogueJewelleryNecklace':
        case 'CatalogueJewelleryRing':
            return `${product.productName} • ${product.primaryMetal.translation.name}`
        case 'CatalogueReadyToGoRing':
            return `${product.productName}`
        default:
            return assertNever(product, undefined)
    }
}

export function useProductPageMeta(
    meta: Readonly<Ref<MetaFragment | undefined | null>>,
    product: Readonly<Ref<ProductPageMetaFragment | undefined | null>>,
    fetching: Ref<boolean>,
    stale: Ref<boolean>,
    rb?: Readonly<Ref<RingBuilder | undefined | null>>
): void {
    useMeta(
        {
            title: (): string | undefined => {
                if (product.value?.customTitleTag) {
                    return product.value.customTitleTag
                }

                const productName = getTitleForProduct(product.value)

                if (productName) {
                    return meta.value?.title
                        .replace('{{name}}', productName)
                        .replace('|', '•')
                }

                return undefined
            },
            description: () =>
                meta.value?.description.replace(
                    '{{description}}',
                    product.value?.translation.description || ''
                ),
        },
        product,
        fetching,
        stale
    )

    const route = useRoute()

    const absoluteUrl = useAbsoluteUrl()

    const bestRating = 5 as const
    const worstRating = 1 as const
    useSchemaOrg([
        computed(() => {
            const currentTime = new Date()
            currentTime.setDate(currentTime.getDate() + 14)
            const url = absoluteUrl(route).href

            const image: string[] = []
            let sku: string | undefined | null
            let price: Money | undefined | null

            if (rb?.value) {
                if (rb.value.imageFor3rdStep.value) {
                    image.push(rb.value.imageFor3rdStep.value.large_x2.url)
                } else {
                    for (const productImage of product.value?.images ?? []) {
                        image.push(productImage.url)
                    }
                }

                sku = rb.value.productId.value

                price = rb.value.salePrice.value?.total
            } else {
                for (const productImage of product.value?.images ?? []) {
                    image.push(productImage.url)
                }

                if (product.value?.__typename === 'CatalogueReadyToGoRing') {
                    sku = product.value?.sku
                } else {
                    sku = product.value?.catalogueCode
                }

                price = product.value?.salePrice.total
            }

            return defineProduct({
                name: product.value?.productName || '',
                image,
                description: product.value?.translation.description ?? '',
                url,
                ...(sku ? { sku, mpn: sku } : {}),
                offers: [
                    {
                        type: 'Offer',
                        url,
                        price: parseFloat(price?.amount ?? '0'),
                        priceCurrency: price?.currency,
                        priceValidUntil: currentTime.toISOString(),
                        itemCondition:
                            'https://schema.org/NewCondition' as const,
                        availability: 'https://schema.org/InStock' as const,
                    },
                ],
                ...(product.value?.reviews.length
                    ? {
                          aggregateRating: {
                              worstRating,
                              bestRating,
                              ratingCount: product.value?.reviews.length,
                              reviewCount: product.value?.reviews.length,
                              ratingValue:
                                  product.value.reviews.reduce(
                                      (acc, item) => acc + item.rating,
                                      0
                                  ) / product.value.reviews.length,
                          },
                          review: product.value?.reviews.map(
                              (review): Review => {
                                  const datePublished = parseDateTime(
                                      review.date
                                  )

                                  return {
                                      reviewRating: {
                                          ratingValue: review.rating,
                                          bestRating,
                                          worstRating,
                                      },
                                      author:
                                          review.name ||
                                          'Taylor & Hart Customer',
                                      datePublished:
                                          datePublished.toISOString(),
                                      reviewBody: review.comment,
                                      image:
                                          (review.picture && {
                                              url: review.picture.url,
                                              contentUrl: review.picture.url,
                                          }) ||
                                          undefined,
                                  }
                              }
                          ),
                      }
                    : {}),
            })
        }),
    ])
}
